import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Tag, Input, Select, Dropdown, message, Modal
} from 'antd';
import {
  PlusOutlined, SearchOutlined, MoreOutlined, EditOutlined,
  DeleteOutlined, BarChartOutlined, FileTextOutlined
} from '@ant-design/icons';
import moment from 'moment';
import EmailCampaignService from '../../services/EmailCampaignService';
import ROUTES from '../../constants/routes';
import { PageContainer, StatsRow, StatCard, StyledTable, PaginationText } from '../Surveys/styled';
import useResponsive from '../../hooks/useResponsive';
import MobileCardList from '../../components/Responsive/MobileCardList';
import ResponsiveFilterBar from '../../components/Responsive/ResponsiveFilterBar';

const STATUS_COLORS = {
  draft: 'default',
  scheduled: 'blue',
  sending: 'processing',
  sent: 'green',
  failed: 'red',
  archived: 'red'
};

const EmailCampaignsPage = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState();

  const load = useCallback(async () => {
    setLoading(true);
    const result = await EmailCampaignService.listCampaigns({
      page: pagination.current,
      limit: pagination.pageSize,
      search,
      status: statusFilter
    });
    if (result.success) {
      setCampaigns(result.data.campaigns || []);
      setPagination((p) => ({ ...p, total: result.data.pagination?.total || 0 }));
    } else {
      message.error(result.error);
    }
    setLoading(false);
  }, [pagination.current, pagination.pageSize, search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = (campaign) => {
    Modal.confirm({
      title: `Delete campaign "${campaign.name}"?`,
      content: 'This will permanently delete the campaign and all recipient data.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        const result = await EmailCampaignService.deleteCampaign(campaign._id);
        if (result.success) {
          message.success('Campaign deleted');
          load();
        } else {
          message.error(result.error);
        }
      }
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name, c) => (
        <a onClick={() => navigate(`/admin/campaigns/${c._id}/edit`)}>
          <strong>{name}</strong>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>{c.subject}</div>
        </a>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (status) => <Tag color={STATUS_COLORS[status]}>{status?.toUpperCase()}</Tag>
    },
    {
      title: 'Linked Survey',
      dataIndex: 'linkedSurveyId',
      width: 200,
      render: (s) => s?.title ? <Tag color="purple">{s.title}</Tag> : '—'
    },
    {
      title: 'Recipients',
      dataIndex: ['stats', 'totalRecipients'],
      width: 110,
      render: (n) => n || 0
    },
    {
      title: 'Sent',
      dataIndex: ['stats', 'sent'],
      width: 80,
      render: (n) => n || 0
    },
    {
      title: 'Opened',
      dataIndex: ['stats', 'opened'],
      width: 90,
      render: (n) => n || 0
    },
    {
      title: 'Clicked',
      dataIndex: ['stats', 'clicked'],
      width: 90,
      render: (n) => n || 0
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      width: 130,
      render: (d) => d ? moment(d).format('MMM D, YYYY') : '—'
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, c) => (
        <Dropdown
          menu={{
            items: [
              { key: 'edit', icon: <EditOutlined />, label: 'Edit', onClick: () => navigate(`/admin/campaigns/${c._id}/edit`) },
              { key: 'analytics', icon: <BarChartOutlined />, label: 'Analytics', onClick: () => navigate(`/admin/campaigns/${c._id}/analytics`) },
              { type: 'divider' },
              { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true, onClick: () => handleDelete(c) }
            ]
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      )
    }
  ];

  const totalSent = campaigns.reduce((sum, c) => sum + (c.stats?.sent || 0), 0);
  const totalOpened = campaigns.reduce((sum, c) => sum + (c.stats?.opened || 0), 0);

  return (
    <PageContainer>
      <StatsRow>
        <StatCard><div className="label">Total Campaigns</div><div className="value">{pagination.total}</div></StatCard>
        <StatCard><div className="label">Emails Sent (page)</div><div className="value">{totalSent}</div></StatCard>
        <StatCard><div className="label">Opens (page)</div><div className="value">{totalOpened}</div></StatCard>
      </StatsRow>

      <ResponsiveFilterBar
        activeFilterCount={[statusFilter].filter(Boolean).length}
        search={(
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search campaigns..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280, height: 40 }}
            allowClear
          />
        )}
        filters={[
          <Select
            key="status"
            placeholder="Status"
            style={{ width: 180, height: 40 }}
            allowClear
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { label: 'Draft', value: 'draft' },
              { label: 'Scheduled', value: 'scheduled' },
              { label: 'Sending', value: 'sending' },
              { label: 'Sent', value: 'sent' },
              { label: 'Failed', value: 'failed' }
            ]}
          />
        ]}
        secondaryActions={[{
          key: 'templates',
          label: 'Templates',
          icon: <FileTextOutlined />,
          onClick: () => navigate(ROUTES.ADMIN_EMAIL_TEMPLATES)
        }]}
        primaryAction={{
          label: 'New Campaign',
          icon: <PlusOutlined />,
          onClick: () => navigate(ROUTES.ADMIN_EMAIL_CAMPAIGN_BUILDER_NEW),
          style: { background: '#E31B23', borderColor: '#E31B23' }
        }}
      />

      {isMobile ? (
        <MobileCardList
          records={campaigns}
          rowKey="_id"
          loading={loading}
          onRowClick={(c) => navigate(`/admin/campaigns/${c._id}/edit`)}
          emptyText="No campaigns yet"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize }))
          }}
          renderCard={(c) => (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</div>
                  {c.subject && (
                    <div style={{ fontSize: 12, color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.subject}</div>
                  )}
                </div>
                <Tag color={STATUS_COLORS[c.status]} style={{ flexShrink: 0 }}>{c.status?.toUpperCase()}</Tag>
              </div>
              {c.linkedSurveyId?.title && (
                <div style={{ marginTop: 8 }}>
                  <Tag color="purple">{c.linkedSurveyId.title}</Tag>
                </div>
              )}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 8,
                marginTop: 12,
                fontSize: 12
              }}>
                <div><div style={{ color: '#8c8c8c' }}>Recipients</div><div style={{ fontWeight: 600 }}>{c.stats?.totalRecipients || 0}</div></div>
                <div><div style={{ color: '#8c8c8c' }}>Sent</div><div style={{ fontWeight: 600 }}>{c.stats?.sent || 0}</div></div>
                <div><div style={{ color: '#8c8c8c' }}>Opened</div><div style={{ fontWeight: 600 }}>{c.stats?.opened || 0}</div></div>
                <div><div style={{ color: '#8c8c8c' }}>Clicked</div><div style={{ fontWeight: 600 }}>{c.stats?.clicked || 0}</div></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {c.createdAt ? moment(c.createdAt).format('MMM D, YYYY') : ''}
                </span>
                <Dropdown
                  menu={{
                    items: [
                      { key: 'edit', icon: <EditOutlined />, label: 'Edit', onClick: () => navigate(`/admin/campaigns/${c._id}/edit`) },
                      { key: 'analytics', icon: <BarChartOutlined />, label: 'Analytics', onClick: () => navigate(`/admin/campaigns/${c._id}/analytics`) },
                      { type: 'divider' },
                      { key: 'delete', icon: <DeleteOutlined />, label: 'Delete', danger: true, onClick: () => handleDelete(c) }
                    ]
                  }}
                  trigger={['click']}
                >
                  <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                </Dropdown>
              </div>
            </div>
          )}
        />
      ) : (
        <StyledTable
          columns={columns}
          dataSource={campaigns}
          rowKey="_id"
          loading={loading}
          size="middle"
          scroll={{ x: 800 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => (
              <PaginationText>Showing {range[0]}-{range[1]} of {total} campaigns</PaginationText>
            ),
            onChange: (page, pageSize) => setPagination((p) => ({ ...p, current: page, pageSize }))
          }}
        />
      )}
    </PageContainer>
  );
};

export default EmailCampaignsPage;
